// src/ApiPanel/pages/txn/TransactionPages.jsx
// The Transaction Report screens.
//
// Each one is a thin config over TransactionReport. The nav items that came
// from the reference screenshots but have no endpoint behind them get
// NoBackendYet instead of a screen that silently shows nothing — a report
// that renders an empty table is indistinguishable from a report with no
// transactions, and on a financial panel that is the wrong thing to be
// ambiguous about.
import React from "react";
import TransactionReport, { NoBackendYet } from "../../components/reports/TransactionReport";
import { Notice } from "../../components/reports/ReportKit";
import { API_PANEL_ENDPOINTS } from "../../constants/apiPanelAuth";

const E = API_PANEL_ENDPOINTS;

/* ── backed by real endpoints ─────────────────────────────────────────── */

export function RechargeDthReport() {
  return (
    <TransactionReport
      title="PrePaid & DTH Report"
      subtitle="Mobile, DTH and FASTag recharges."
      endpoint={E.TXN_RECHARGE}
    />
  );
}

export function BillPaymentReport() {
  return (
    <TransactionReport
      title="Bill Payment Report"
      subtitle="Online BBPS bill payments."
      endpoint={E.TXN_BBPS}
    />
  );
}

export function BillPaymentDraftReport() {
  return (
    <TransactionReport
      title="Bill Payment Draft"
      subtitle="Offline BBPS payments — raised but not yet settled through the biller."
      endpoint={E.TXN_OFFLINE_BBPS}
    />
  );
}

export function UpiQrReport() {
  return (
    <TransactionReport
      title="UPI QR Report"
      subtitle="Collections through dynamic UPI QR."
      endpoint={E.TXN_UPI_QR}
      notice={
        <Notice tone="info">
          Your nav called this &ldquo;Kotak Qr Report&rdquo;. The API exposes UPI QR
          collections without splitting them by acquiring bank, so this is the
          whole QR book. If Kotak needs its own view, the endpoint needs a bank
          filter first.
        </Notice>
      }
    />
  );
}

export function AepsReport() {
  return (
    <TransactionReport
      title="AEPS Report"
      subtitle="Aadhaar Enabled Payment System — withdrawals, balance enquiries and mini statements."
      endpoint={E.TXN_AEPS}
      notice={
        <Notice tone="warn">
          Aadhaar numbers and card numbers are masked to the last four digits.
          If you need a full value for a dispute, pull it from the transaction
          receipt with the reference number — not from a report anyone with the
          screen open can read.
        </Notice>
      }
    />
  );
}

export function MatmReport() {
  return (
    <TransactionReport
      title="Micro ATM Report"
      subtitle="Card-based withdrawals and balance enquiries through mATM."
      endpoint={E.TXN_MATM}
    />
  );
}

export function DmtReport() {
  return (
    <TransactionReport
      title="DMT Report"
      subtitle="Domestic money transfers."
      endpoint={E.TXN_DMT}
    />
  );
}

export function CreditDebitReport() {
  return (
    <TransactionReport
      title="Credit / Debit Report"
      subtitle="Wallet movements — funds in, funds out, and the balance either side of each."
      endpoint={E.TXN_CREDIT_DEBIT}
    />
  );
}

export function PanCardTxnHistory() {
  return (
    <TransactionReport
      title="PAN CARD TXN History"
      subtitle="PAN card applications raised through the platform."
      endpoint={E.TXN_PANCARD}
      notice={
        <Notice tone="warn">
          This report carries applicant PII — name, date of birth, parent&rsquo;s
          name, and links to uploaded identity documents. Document links are not
          rendered here on purpose. Treat the screen as restricted.
        </Notice>
      }
    />
  );
}

/* ── in the nav, no endpoint behind them ──────────────────────────────── */

export const PayoutReport = () => (
  <NoBackendYet title="PAYOUT Report" expected="a payout-product filter over the transaction table" />
);
export const RechargeDispute = () => (
  <NoBackendYet title="Recharge Dispute" expected="a list view over mobile_recharge_complain" />
);
export const NortonReport = () => <NoBackendYet title="Norton Report" />;
export const CashDepositReport = () => (
  <NoBackendYet title="Cash Deposit Report" expected="a CMS/cash-deposit product filter" />
);
export const ManualPancardRequest = () => (
  <NoBackendYet title="Manual Pancard Request" expected="a queue over PanCardRequest" />
);
export const MoneySuccToFailed = () => (
  <NoBackendYet title="Money Succ_to_failed" expected="an audit view of admin status changes" />
);
export const MoneyFailedToSucc = () => (
  <NoBackendYet title="Money Failed_to_succ" expected="an audit view of admin status changes" />
);
export const AadharVerifyReport = () => (
  <NoBackendYet title="Aadhar Verify Report" expected="a report over the KYC verification log" />
);
export const PanVerifyReport = () => (
  <NoBackendYet title="Pan Verify Report" expected="a report over the KYC verification log" />
);
export const AxisAccountOpen = () => <NoBackendYet title="Axis Account Open" />;
export const FinoCmsReport = () => (
  <NoBackendYet title="Fino CMS Report" expected="a CMS product filter scoped to the Fino partner" />
);
